import { distinctUntilChanged, share } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Observer, Subscription } from 'rxjs';
import {
  ChatCommand,
  ChatCommandJoin,
  ChatCommandLoad,
  ChatCommandLoadMore,
  ChatCommandSend,
  ChatCommandTyping,
  ChatCommandUnitRoomActivity,
} from '../classes/websocketChatCommands';
import { Message } from '../classes/message';
import { UnitRoom, UnitRoomActivity } from '../classes/unitRoom';
import { User } from '../classes/user';

import { environment } from '../../../environments/environment';

@Injectable()
export class WebSocketService implements OnDestroy {
  private socket$: WebSocket;

  private hasSetUnitRoomObserver: Observer<boolean>;
  private messagesObserver: Observer<Message[]>;
  private showTypingObserver: Observer<boolean>;
  private willLoadMoreObserver: Observer<boolean>;
  private didLoadMoreObserver: Observer<boolean>;
  private didSendMessageObserver: Observer<boolean>;
  private didReceiveMessageObserver: Observer<boolean>;

  private _previous?: number;
  private _next?: number;
  private _reached_end?: boolean;
  private currentUser: User;
  private messagesObservableSubscription$: Subscription;

  public unit_room_id?: number;
  public unit_room?: UnitRoom;
  public is_loading_more: boolean = false;
  public messages: Message[];
  public unit_room_activities: UnitRoomActivity[];
  public messagesObservable?: Observable<Message[]>;
  public hasSetUnitRoomStatus?: Observable<boolean>;
  public showTypingStatus?: Observable<boolean>;
  public willLoadMoreStatus?: Observable<boolean>;
  public didLoadMoreStatus?: Observable<boolean>;
  public didSendMessageStatus?: Observable<boolean>;
  public didReceiveMessageStatus?: Observable<boolean>;

  constructor() {
    this.hasSetUnitRoomStatus = new Observable<boolean>(observer => {
      this.hasSetUnitRoomObserver = observer;
      this.hasSetUnitRoomObserver.next(false);
    }).pipe(
      share(),
      distinctUntilChanged()
    );

    this.messagesObservable = new Observable<Message[]>(observer => {
      this.messagesObserver = observer;
      this.messagesObserver.next([]);
    }).pipe(
      share(),
      distinctUntilChanged()
    );

    this.showTypingStatus = new Observable<boolean>(observer => {
      this.showTypingObserver = observer;
      this.showTypingObserver.next(false);
    }).pipe(
      share(),
      distinctUntilChanged()
    );

    this.willLoadMoreStatus = new Observable<boolean>(observer => {
      this.willLoadMoreObserver = observer;
    }).pipe(share());

    this.didLoadMoreStatus = new Observable<boolean>(observer => {
      this.didLoadMoreObserver = observer;
    }).pipe(share());

    this.didSendMessageStatus = new Observable<boolean>(observer => {
      this.didSendMessageObserver = observer;
    }).pipe(share());

    this.didReceiveMessageStatus = new Observable<boolean>(observer => {
      this.didReceiveMessageObserver = observer;
    }).pipe(share());

    this.messagesObservableSubscription$ = this.messagesObservable.subscribe(
      messages => {
        if (messages) {
          this.messages = messages;
        }
      }
    );
  }

  public initChatRoom(unit_room_id: number, unitRoom: any) {
    if (this.socket$ != null) {
      if (this.unit_room_id !== unit_room_id) {
        // If we have a websocket, but different company room
        this.messagesObserver.next([]);
        this.hasSetUnitRoomObserver.next(false);
        this.socket$.onclose = null;
        this.socket$.close();
      } else {
        // If we have connection to the same company room
        this.hasSetUnitRoomObserver.next(true);
        return;
      }
    }

    // Showing name of applicant,
    // Avoids chat getting very tall
    this.unit_room = unitRoom;
    // Important for knowing when to close socket
    this.unit_room_id = unit_room_id;

    const access_token = localStorage.getItem('id_token');
    if (!access_token) {
      this.messagesObserver.next([]);
      this.hasSetUnitRoomObserver.next(false);
      this.socket$.onclose = null;
      this.socket$.close();
      return;
    }

    this.socket$ = new WebSocket(
      environment.chat_websocket_url + '?access_token=' + access_token
    ); // new WebSocketSubject(wsSubjectConfig);

    this.socket$.onopen = event => {
      if (this.socket$ != null) {
        this.sendJoinUnitRoom();
      }
    };

    this.socket$.onclose = event => {
      this.socket$ = null;
      this.hasSetUnitRoomObserver.next(false);
    };

    // this.socket$.onerror()

    this.socket$.onmessage = event => {
      console.log(JSON.parse(event.data));
      const message = event.data;
      const parsedMessage = JSON.parse(message);
      // let chatCommand: ChatCommand = ObjectMapper.deserialize(new ChatCommand(""), message);
      const chatCommand: ChatCommand =
        typeof message != null ? parsedMessage : null;
      if (chatCommand && chatCommand.command) {
        switch (chatCommand.command) {
          case 'load':
            const chatCommandLoad: ChatCommandLoad = parsedMessage;
            this.handleChatCommandLoad(chatCommandLoad);
            break;
          case 'send':
            const chatCommandSend: ChatCommandSend = parsedMessage;
            this.handleChatCommandSend(chatCommandSend);
            break;
          case 'typing':
            const chatCommandTyping: ChatCommandTyping = parsedMessage;
            this.handleChatCommandTyping(chatCommandTyping);
            break;
          case 'load_more':
            const chatCommandLoadMore: ChatCommandLoadMore = parsedMessage;
            this.handleChatCommandLoadMore(chatCommandLoadMore);
            break;
          case 'unit_room_activity':
            const chatCommandUnitRoomActivity: ChatCommandUnitRoomActivity = parsedMessage;
            this.handleChatCommandUnitRoomActivity(chatCommandUnitRoomActivity);
            break;
        }
      }
    };
  }

  public disconnect() {
    if (this.socket$ != null) {
      this.socket$.close();
      this.socket$ = null;
    }
    this.is_loading_more = false;
    this._previous = null;
    this._next = null;
    this._reached_end = null;
    this.messagesObserver.next([]);
    this.hasSetUnitRoomObserver.next(false);
    this.showTypingObserver.next(false);
  }

  private handleChatCommandTyping(chatCommandTyping: ChatCommandTyping) {
    if (chatCommandTyping.status === 'start') {
      this.showTypingObserver.next(true);
    } else {
      this.showTypingObserver.next(false);
    }
  }

  private handleChatCommandLoadMore(chatCommandLoadMore: ChatCommandLoadMore) {
    this.willLoadMoreObserver.next(true);
    this._previous = chatCommandLoadMore.previous;
    this._next = chatCommandLoadMore.next;
    this._reached_end = chatCommandLoadMore.reached_end;
    const updatedMessagesList = chatCommandLoadMore.messages.concat(
      this.messages
    );
    this.messagesObserver.next(updatedMessagesList);
    this.is_loading_more = false;
    this.didLoadMoreObserver.next(true);
  }

  private handleChatCommandSend(chatCommandSend: ChatCommandSend) {
    let tmp_messages = this.messages;
    if (
      chatCommandSend.message.author !== this.currentUser.id ||
      (chatCommandSend.message.type !== 'Message' &&
        chatCommandSend.message.type !== 'NoteMessage')
    ) {
      // Message from current user has already been appended
      // only for Message and NoteMessage
      tmp_messages.push(chatCommandSend.message);
      this.didReceiveMessageObserver.next(true);
    } else {
      tmp_messages = tmp_messages.map(m => {
        m.is_waiting = false;
        return m;
      });
    }
    this.unit_room.latest_message = chatCommandSend.message;
    this.messagesObserver.next(tmp_messages);
  }

  private handleChatCommandUnitRoomActivity(
    chatCommandUnitRoomActivity: ChatCommandUnitRoomActivity
  ) {
    this.unit_room_activities =
      chatCommandUnitRoomActivity.unit_room_activities;
  }

  private handleChatCommandLoad(chatCommandLoad: ChatCommandLoad) {
    this._previous = chatCommandLoad.previous;
    this._next = chatCommandLoad.next;
    this._reached_end = chatCommandLoad.reached_end;
    this.unit_room = chatCommandLoad.unit_room;
    this.messagesObserver.next(chatCommandLoad.messages);
    this.hasSetUnitRoomObserver.next(true);
    this.is_loading_more = false;
  }

  private socket_send(data: any): void {
    const stringified_object = JSON.stringify(data);
    this.socket$.send(stringified_object);
  }

  private sendJoinUnitRoom() {
    const commandJoin = new ChatCommandJoin(this.unit_room_id);
    this.socket_send(commandJoin);
  }

  public sendLoadMore() {
    if (this._reached_end) {
      return;
    }
    if (!this.is_loading_more) {
      this.is_loading_more = true;
      const commandLoadMore = new ChatCommandLoadMore(
        this._previous,
        this._next,
        this.unit_room_id
      );
      this.socket_send(commandLoadMore);
    }
  }

  public sendMessage(message: Message) {
    const tmp_messages = this.messages;
    const now = new Date();
    const newMessage = { ...message, created_at: now.toISOString() };
    tmp_messages.push(newMessage);
    this.messagesObserver.next(tmp_messages);
    this.didSendMessageObserver.next(true);
    const commandSend = new ChatCommandSend(this.unit_room_id, message);
    this.socket_send(commandSend);
  }

  public setCurrentUser(user: User) {
    this.currentUser = user;
  }

  ngOnDestroy() {
    this.messagesObservableSubscription$.unsubscribe();
  }
}
